import React from "react";
import { Avatar, Dropdown, Modal,Grid } from "antd";
import {
  AccountBookOutlined,
  PoweroffOutlined,
  SyncOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { createUseStyles } from "react-jss";
import { useAuth0 } from "@auth0/auth0-react";
import LanguageSelector from "../../components/selectors/language/LanguageSelector";
import AppSelector from "../../components/selectors/app/AppSelector";
import { useGetSelfEmployeeQuery } from "../../store/api/employeeApi";
import { EmployeeUpdateDTO } from "../../types/auth/EmployeeUpdateDTO";
import { AccountDTO } from "../../types/account/AccountDTO";
import { ItemType } from "antd/es/menu/hooks/useItems";
import NavigateButton from "../../components/buttons/NavigateButton";
import { useSynchronizeMutation } from "../../store/api/accountApi";
import { wrapResponse } from "../../types/utils/Constants";
import { ListProps } from "./Header";

const { useBreakpoint } = Grid;

const useStyles = createUseStyles(() => ({
  firstNavBlock: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "15px",
  },
  dropDownChild: {
    overflow: "auto",
    height: "75vh",
  },
}));

interface HeaderRightBlockProps {
  extra?: React.ReactNode;
  updateEmployee: (dto: EmployeeUpdateDTO) => void;
  list: ListProps[];
  setSection: (section: ListProps) => void;
}

const HeaderRightBlock: React.FC<HeaderRightBlockProps> = ({
  extra,
  updateEmployee,
  list,
  setSection,
}) => {
  const classes = useStyles();
  const { user, logout } = useAuth0();
  const screens = useBreakpoint();
  const { data: employee } = useGetSelfEmployeeQuery();
  const [sync] = useSynchronizeMutation();

  const syncHandler = () => wrapResponse(sync().unwrap());

  const changeAccount = async (accountId: number) => {
    const accounts = employee?.data?.accounts?.map((item) => ({
      account_id: item.id,
      main: item.id === accountId,
    }));

    if (
      !employee?.data?.accounts?.filter((item) => item.id === accountId)?.at(0)
    )
      accounts?.push({ account_id: accountId, main: true });

    updateEmployee({
      id: employee?.data?.id,
      picture_url: employee?.data?.picture_url,
      accounts,
    });
    window.location.reload();
  };

  // Выйти из системы
  const onLogoutHandler = () => {
    Modal.confirm({
      title: "Вы действительно хотите выйти?",
      onOk: () => logout({ returnTo: window.origin }),
    });
  };

  const onAccountHandler = (accountId: number) => {
    Modal.confirm({
      title: "Вы действительно хотите сменить аккаунт?",
      onOk: () => changeAccount(accountId),
    });
  };

  const mapToDropDownItem = (item: AccountDTO): ItemType => ({
    key: item.id,
    label: item.name,
    disabled: item.id === employee?.data?.account?.id,
    onClick: () => onAccountHandler(item.id),
  });

  const flatMapAccount = (item: AccountDTO): ItemType[] => [
    mapToDropDownItem(item),
    ...item.child_accounts.map((child) => flatMapAccount(child)).flat(),
  ];

  const items = [
    {
      label: !employee?.data?.accounts.length ? "Нет доступных аккаунтов" : (employee?.data?.account?.name ?? "Выберите аккаунт"),
      key: "account",
      icon: <AccountBookOutlined />,
      children: employee?.data?.accounts
        ?.map((item) => flatMapAccount(item))
        .flat(),
      popupClassName: classes.dropDownChild,
      disabled: employee?.data?.accounts.length === 0,
    },
    {
      label: "Синхронизация",
      key: "sync",
      icon: <SyncOutlined />,
      onClick: syncHandler,
      disabled: !employee?.data?.permission?.can_synchronize_accounts,
    },
    {
      label: "Выйти",
      key: "logout",
      icon: <PoweroffOutlined />,
      onClick: onLogoutHandler,
    },
  ];

  return (
    <div className={classes.firstNavBlock}>
      {screens.xs ? "" : extra}
      {list
        .filter((it) => !it.hiddenInHeader)
        .map((it) => (
          <NavigateButton key={it.link} setSection={setSection} section={it} />
        ))}
      {screens.xs ? "" : <LanguageSelector />}
      {screens.xs ? "" : <AppSelector />}
      <Dropdown menu={{ items }} arrow>
        <div>
          <Avatar
            icon={<UserOutlined />}
            src={user?.picture ?? employee?.data?.picture_url}
            style={{ verticalAlign: "middle", background: "#016f4d" }}
          />
        </div>
      </Dropdown>
    </div>
  );
};

export default HeaderRightBlock;
